import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Tabs, Button, Modal, Radio, DatePicker, message } from "antd";
import { Map, List, OrderedMap } from "immutable";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import { Table } from "components/table";
import { CIBIL_EDITABLE_COLS, CIBIL_READ_ONLY_COLS } from "constants/table-columns";
import SaveProxy from "utilities/save-proxy";
import { INPUT_TYPES } from "constants/input-types";
import DocumentDrawer from "components/documents-drawer";
import {
    fetchCibilDetails,
    updateCibilDetails
} from "actions";
import { STATE_STATUS } from "constants/state-status";
import CibilService from "services/cibil-service";
import { SCORE_CARD_RANGE, CIBIL_CONDITIONS, CIBIL_COMMENTS } from "constants/enums";
import "styles/cibil-page.css";

const { TabPane } = Tabs;
const RadioGroup = Radio.Group;

export const onCellGenerator = (handleSave, _) => record => ({
    record,
    editable: true,
    dataIndex: _.dataIndex,
    title: record.header || _.title,
    handleSave,
    rules: record.rules || _.rules,
    fieldType: record.fieldType || _.fieldType,
    filesMultiple: _.filesMultiple ? _.filesMultiple : false,
    options: record.options || _.options,
    getFileUploadParams: _.getFileUploadParams,
    uploadTarget: _.uploadTarget,
    viewable: _.viewable,
    max: record.max
});

const CIBIL_PENDING_APPROVAL_COLS = new OrderedMap({
    case: {
        title: "Case ID",
        key: "case",
        dataIndex: "case",
        width: 100,
        render: _ => _ && `PGCP-${_}`
    },
    "associate_data.company_associate": {
        title: "Name",
        key: "associate_data.company_associate",
        dataIndex: "associate_data.company_associate",
        width: 150,
        render: (_) => _ && `${_.first_name} ${_.last_name}`
    },
    "associate_data.kyc_docs.pan": {
        title: "PAN",
        key: "associate_data.kyc_docs.pan",
        dataIndex: "associate_data.kyc_docs.pan",
        editable: false,
        width:100,
    },
    "associate_data.kyc_docs.aadhar": {
        title: "Aadhaar",
        key: "associate_data.kyc_docs.aadhar",
        dataIndex: "associate_data.kyc_docs.aadhar",
        editable: false,
        width:100,
    },
    date_of_submission: {
        title: "Date of Submission",
        key: "date_of_submission",
        dataIndex: "date_of_submission",
        editable: false,
        width: 100,
    },
    "associate_data.cibil_score": {
        title: "Credit Vision Score",
        key: "associate_data.cibil_score",
        dataIndex: "associate_data.cibil_score",
        width: 150,
    },
    "associate_data.personal_cibil_score": {
        title: "Personal Loan Score",
        key: "associate_data.personal_cibil_score",
        dataIndex: "associate_data.personal_cibil_score",
        width: 150,
    },
    "associate_data.cibil_score_range": {
        title: "CIBIL Score Range",
        key: "associate_data.cibil_score_range",
        dataIndex: "associate_data.cibil_score_range",
        width: 150,
        render: __ => SCORE_CARD_RANGE.findKey(_ => _ == __)
    },
    "associate_data.crif_score": {
        title: "CRIF Score",
        key: "associate_data.crif_score",
        dataIndex: "associate_data.crif_score",
        width: 150,
    },
    "associate_data.cibil_os": {
        title: "CIBIL Outstanding",
        key: "associate_data.cibil_os",
        dataIndex: "associate_data.cibil_os",
        width: 150,
    },
    "associate_data.credit_check.comments": {
        title: "Bank Comments",
        key: "associate_data.credit_check.comments",
        dataIndex: "associate_data.credit_check.comments",
        editable: true,
        onCellGenerator,
        width: 150,
        fieldType: INPUT_TYPES.SELECT,
        options: CIBIL_COMMENTS.map((_, k) => ({label: k, value: _})).toList().toArray()
    },
    "associate_data.credit_check.conditions": {
        title: "Conditions",
        key: "associate_data.credit_check.conditions",
        dataIndex: "associate_data.credit_check.conditions",
        editable: true,
        onCellGenerator,
        width: 150,
        fieldType: INPUT_TYPES.SELECT,
        options: CIBIL_CONDITIONS.map((_, k) => ({
            label: k,
            value: _
        })).toList().toArray()
    },
    "associate_data.credit_check.descriptions": {
        title: "Description",
        key: "associate_data.credit_check.descriptions",
        dataIndex: "associate_data.credit_check.descriptions",
        editable: true,
        onCellGenerator,
        width: 250,
        fieldType: INPUT_TYPES.TEXTAREA
    }
});

const START_DATE = "modified_date__date";
const END_DATE = "modified_date__date__lte";

class CibilTable extends React.Component {
    state = {
        associates: this.props.associates || new List([]),
        delta: new Map({}),
        isAssociatesChanged: false,
        selectedTab: "open",
        currentPage: 1,
        [START_DATE]: null,
        [END_DATE]: null,
        showFilterModal: false
    };

    pageSize = 20;

    componentDidMount() {
        this.props.fetchCibilDetails(this.state.selectedTab, this.state.currentPage, this.pageSize, {});
        SaveProxy.attach(1, this.updateAssociates);
    }

    componentWillUnmount() {
        SaveProxy.detach(1);
    }

    handleDateInput = (type, _) => {
        if (
            type === END_DATE &&
            this.state[START_DATE] &&
            this.state[START_DATE].isAfter(_)
        ) {
            return;
        }
        if (
            type === START_DATE &&
            this.state[END_DATE] &&
            this.state[END_DATE].isBefore(_)
        ) {
            return;
        }
        this.setState({
            [type]: _
        });
    };

    toggleFilterModal = () => {
        this.setState({
            showFilterModal: !this.state.showFilterModal
        });
    };

    base64ToArrayBuffer = data => {
        var input = data.substring(data.indexOf(",") + 1);
        var binaryString = window.atob(input);
        var binaryLen = binaryString.length;
        var bytes = new Uint8Array(binaryLen);
        for (var i = 0; i < binaryLen; i++) {
            var ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    };

    base64ToArrayBuffer = data => {
        var input = data.substring(data.indexOf(",") + 1);
        var binaryString = window.atob(input);
        var binaryLen = binaryString.length;
        var bytes = new Uint8Array(binaryLen);
        for (var i = 0; i < binaryLen; i++) {
            var ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    };

    downloadCibilData = () => {
        message.loading("Downloading file...", 0);
        CibilService.downloadCibilData().then(res => {
            message.destroy();
            const data = this.base64ToArrayBuffer(res.data);
            var blob = new Blob([data], { type: "application/vnd.ms-excel" });
            var elem = window.document.createElement("a");
            elem.href = window.URL.createObjectURL(blob);
            elem.download = "cibil-requests.xlsx";
            document.body.appendChild(elem);
            elem.click();
            document.body.removeChild(elem);

            // concjp;
        });
    };

    filterStatement = () => {
        // const { facilityId, fetchAccountStmtList } = this.props;
        if (this.state.filterValue) {
            if (this.state.filterValue === "1-month") {
                this.setState({
                    [START_DATE]: moment().subtract(1, "months"),
                    [END_DATE]: moment()
                });
            }
            if (this.state.filterValue === "3-months") {
                this.setState({
                    [START_DATE]: moment().subtract(3, "months"),
                    [END_DATE]: moment()
                });
            }
        }
        this.setState(
            {
                showFilterModal: false,
                currentPage: 1,
            },
            () => {
                this.props.fetchCibilDetails(this.state.selectedTab, this.state.currentPage, this.pageSize, {
                    [START_DATE]: this.state[START_DATE]
                        ? this.state[START_DATE].format("YYYY-MM-DD")
                        : null,
                    [END_DATE]: this.state[END_DATE]
                        ? this.state[END_DATE].format("YYYY-MM-DD")
                        : null
                });
            }
        );
    };

    onChangeFilterValue = e => {
        this.setState({
            filterValue: e.target.value
        });
    };

    clearFilterStatement = () => {
        this.setState(
            {
                [START_DATE]: null,
                [END_DATE]: null,
                filterValue: null
            },
            () => this.filterStatement()
        );
    };

    renderFilterModal = () => {
        return (
            <React.Fragment>
                <Modal
                    title={"Filter"}
                    visible={this.state.showFilterModal}
                    onOk={this.filterStatement}
                    onCancel={this.toggleFilterModal}
                    destroyOnClose={false}>
                    <div className={"d-flex flex-column"}>
                        <RadioGroup
                            onChange={this.onChangeFilterValue}
                            value={this.state.filterValue}>
                            <Radio value={"1-month"}>1-Month</Radio>
                            <Radio value={"3-months"}>3-Months</Radio>
                            <Radio value={"custom"}>Custom Search</Radio>
                        </RadioGroup>
                        {this.state.filterValue === "custom" ? (
                            <div style={{ padding: "15px" }}>
                                <DatePicker
                                    style={{
                                        width: "100%"
                                    }}
                                    disabledDate={value =>
                                        value.valueOf() > moment().valueOf()
                                    }
                                    size={"large"}
                                    className={"mb-5"}
                                    onChange={this.handleDateInput.bind(
                                        null,
                                        START_DATE
                                    )}
                                    value={this.state[START_DATE]}
                                    placeholder={"Start Date"}
                                />
                                <DatePicker
                                    style={{
                                        width: "100%"
                                    }}
                                    disabledDate={value =>
                                        value.valueOf() > moment().valueOf()
                                    }
                                    size={"large"}
                                    className={"mb-5"}
                                    onChange={this.handleDateInput.bind(
                                        null,
                                        END_DATE
                                    )}
                                    value={this.state[END_DATE]}
                                    placeholder={"End Date"}
                                />
                            </div>
                        ) : null}
                        <Button
                            type={"default"}
                            className={"mt-5 link-btn link-btn-dark"}
                            onClick={this.clearFilterStatement}
                            icon={"close"}>
                            Clear Filters
                        </Button>
                    </div>
                </Modal>
            </React.Fragment>
        );
    };

    handleSave = (record, values) => {
        this.setState(
            {
                delta: this.state.delta.set(record.id, values)
            },
            () => SaveProxy.activate(1)
        );
    };

    updateAssociates = () => {
        if (this.state.delta.size) {
            const delta = new Map(this.state.delta);
            const data = delta.map((_, k) => {
                let associate = this.props.associates.find(_ => _.id == k);
                let cibilRecord = {};
                if(this.state.selectedTab === "open") {
                    cibilRecord = {
                        ..._,
                        id: k,
                        associate_data: {
                            ..._.associate_data,
                            id: associate && associate.associate_data && associate.associate_data.id
                        },
                    };
                } else {
                    cibilRecord = {
                        id: k,
                        associate_data: {
                            credit_check: {
                                ...(_.associate_data && _.associate_data.credit_check)
                            },
                            id: associate && associate.associate_data && associate.associate_data.id
                        },
                    };
                }
                return cibilRecord;
            }).toList().toArray();
            this.props.updateCibilDetails(this.state.selectedTab, data);
        }
    };

    updateCibilTable = () => {
        const { status } = this.props;
        const { associates } = this.props;
        return (
            <Table
                columns={CIBIL_EDITABLE_COLS.map(
                    ({
                        onCellGenerator,
                        editable,
                        ...restColFields
                    }) => {
                        if (!onCellGenerator || !editable) {
                            return { ...restColFields };
                        }
                        return {
                            onCell: onCellGenerator(
                                this.handleSave,
                                restColFields
                            ),
                            ...restColFields
                        };
                    }
                )}
                checkedColumns={[]}
                data={associates.map((_ => {
                    let row = _;
                    row["associate_data.kyc_docs.pan"] = (
                        <div className="d-flex flex-column align-items-center">
                            <span>{_.associate_data && _.associate_data.kyc_docs && _.associate_data.kyc_docs.pan}</span>
                            <Button type={"link"} onClick={() => this.showDocument(_, "pan")}>
                                View
                            </Button>
                        </div>
                    );
                    row["associate_data.kyc_docs.aadhar"] = (
                        <div className="d-flex flex-column align-items-center">
                            <span>{_.associate_data && _.associate_data.kyc_docs && _.associate_data.kyc_docs.aadhar}</span>
                            <Button type={"link"} onClick={() => this.showDocument(_, "aadhar")}>
                                View
                            </Button>
                        </div>
                    );
                    return row;
                })).toList()}
                showTitle={false}
                showTopSection={false}
                showFooter={false}
                showPaginationOnly={false}
                pagination={{
                    current: this.state.currentPage,
                    showSizeChanger: true,
                    defaultPageSize: 20,
                    pageSizeOptions: [
                        "10",
                        "20",
                        "50",
                        "100",
                        "500"
                    ],
                    position: "bottom",
                    total: this.props.currentTabCount,
                    onChange: (page, pageSize) =>
                        this.onPageChange(page, pageSize),
                    onShowSizeChange: this.onPageSizeChange,
                }}
                keyFilter={"id"}
                loading={
                    status !== STATE_STATUS.FETCHED &&
                    status !== STATE_STATUS.UPDATED
                }
                editable={true}
                noRowSelection={true}
                className={"inbox-table"}
            />
        );
    }

    showDocument = (obj, type) => {
        this.setState({
            fileUrls: type === "pan" ? obj.associate_data.kyc_docs.pan_docs : obj.associate_data.kyc_docs.aadhar_docs,
            openDocViewer: true
        });
    }

    onPageChange = (page, pageSize) => {
        this.pageSize = pageSize;
        this.setState({
            currentPage: page,
        }, () => {
            this.props.fetchCibilDetails(this.state.selectedTab, this.state.currentPage, this.pageSize, {});
        });
    }

    onPageSizeChange = (page, pageSize) => {
        this.pageSize = pageSize;
        this.setState({
            currentPage: page,
        }, () => {
            this.props.fetchCibilDetails(this.state.selectedTab, this.state.currentPage, this.pageSize, {});
        });
    }

    closedTabTitle = (
        <React.Fragment>
            <div className="d-flex justify-content-space-between">
                <Button onClick={this.toggleFilterModal}>
                    Filter CIBIL Requests
                </Button>
                <Button type={"link"} onClick={this.downloadCibilData}>
                    Download CIBIL Data
                </Button>
            </div>
        </React.Fragment>
    );

    getClosedCibilRequests = () => {
        const { status } = this.props;
        const { associates } = this.props;
        return (
            <Table
                title={this.closedTabTitle}
                columns={CIBIL_READ_ONLY_COLS}
                checkedColumns={[]}
                data={associates.toList()}
                showTitle={false}
                showTopSection={true}
                showFilters={false}
                showColumnSelector={false}
                // showApiXlsxDownload={true}
                // onApiXlsxDownload={this.downloadCibilData}
                showFooter={false}
                showPaginationOnly={false}
                pagination={{
                    current: this.state.currentPage,
                    showSizeChanger: true,
                    defaultPageSize: 20,
                    pageSizeOptions: [
                        "10",
                        "20",
                        "50",
                        "100",
                        "500"
                    ],
                    position: "bottom",
                    total: this.props.currentTabCount,
                    onChange: (page, pageSize) =>
                        this.onPageChange(page, pageSize),
                    onShowSizeChange: this.onPageSizeChange,
                }}
                spanConfig={{
                    title: 24
                }}
                fixedColsLeft={["case"]}
                keyFilter={"id"}
                loading={
                    status !== STATE_STATUS.FETCHED
                }
                editable={false}
                noRowSelection={true}
                className={"inbox-table"}
            />
        );
    }

    updatePendingTable = () => {
        const { status } = this.props;
        const { associates } = this.props;
        return (
            <Table
                columns={CIBIL_PENDING_APPROVAL_COLS.map(
                    ({
                        onCellGenerator,
                        editable,
                        ...restColFields
                    }) => {
                        if (!onCellGenerator || !editable) {
                            return { ...restColFields };
                        }
                        return {
                            onCell: onCellGenerator(
                                this.handleSave,
                                restColFields
                            ),
                            ...restColFields
                        };
                    }
                )}
                checkedColumns={[]}
                data={associates && associates.toList()}
                showTitle={false}
                showTopSection={false}
                showFooter={false}
                showPaginationOnly={false}
                pagination={{
                    current: this.state.currentPage,
                    showSizeChanger: true,
                    defaultPageSize: 20,
                    pageSizeOptions: [
                        "10",
                        "20",
                        "50",
                        "100",
                        "500"
                    ],
                    position: "bottom",
                    total: this.props.currentTabCount,
                    onChange: (page, pageSize) =>
                        this.onPageChange(page, pageSize),
                    onShowSizeChange: this.onPageSizeChange,
                }}
                keyFilter={"id"}
                loading={
                    status !== STATE_STATUS.FETCHED &&
                    status !== STATE_STATUS.UPDATED
                }
                editable={true}
                noRowSelection={true}
                className={"inbox-table"}
            />
        );
    }

    onChangeTab = (e) => {
        this.setState({
            selectedTab: e,
            currentPage: 1,
        }, () => {
            this.forceUpdate();
            this.props.fetchCibilDetails(this.state.selectedTab, this.state.currentPage, this.pageSize, {});
        });
    }

    closeDocViewer = () => {
        this.setState({
            openDocViewer: false
        });
    }

    openDocViewer = () => {
        return (
            <DocumentDrawer
                urls={this.state.fileUrls}
                openDocViewer={this.state.openDocViewer}
                closeDocViewer={this.closeDocViewer}
            />
        );
    }

    render() {
        return (
            <Row className={"p-5"}>
                {this.openDocViewer()}
                {this.renderFilterModal()}
                <Col span={24} className={"p-10"}>
                    <Tabs
                        className={"cibil-tabs"}
                        title={"CIBIL Details"}
                        size={"small"}
                        tabBarExtraContent={["open", "pending-approval"].includes(this.state.selectedTab) ? [
                            <a
                                key={"icon"}
                                onClick={this.updateAssociates}
                            >
                                {" "}
                                    Update
                            </a>
                        ]: []}
                        onChange={this.onChangeTab}
                    >
                        <TabPane tab={"Open Requests"} key="open">
                            {this.updateCibilTable()}
                        </TabPane>
                        <TabPane tab={"Pending Approval"} key="pending-approval">
                            {this.updatePendingTable()}
                        </TabPane>
                        <TabPane tab={"Closed Requests"} key="close">
                            {this.getClosedCibilRequests()}
                        </TabPane>
                    </Tabs>
                </Col>
            </Row>
        );
    }
}

CibilTable.propTypes = {
    associates: PropTypes.object,
    status: PropTypes.string,
    save: PropTypes.bool,
    update: PropTypes.func,
    fetchCibilDetails: PropTypes.func,
    updateCibilDetails: PropTypes.func,
    currentTabCount: PropTypes.number,
};

CibilTable.defaultProps = {
    associates: new Map({}),
    currentTabCount: 0
};

const mapStateToProps = (state) => {
    let currentOpenTab = state.cibilDetails && state.cibilDetails.get("current_tab");
    return {
        status: state.cibilDetails && state.cibilDetails.getIn([currentOpenTab, "status"]),
        associates: state.cibilDetails && state.cibilDetails.getIn([currentOpenTab, "data"]),
        currentTabCount: state.cibilDetails && state.cibilDetails.getIn([currentOpenTab, "count"])
    };    
};

const mapDispatchToProps = dispatch =>
    bindActionCreators({
        fetchCibilDetails,
        updateCibilDetails
    }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CibilTable);